import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  ChevronLeft,
  ChevronRight,
  ImagePlus,
  Loader2,
  Plus,
  Trash2,
  Video,
} from 'lucide-react';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import {
  MEDIA_TYPE,
  useAddMedia,
  useRegistrationMedia,
  useRemoveMedia,
  useReorderMedia,
  useUploadRegistrationMedia,
  type RegistrationMedia,
} from './item-registration.api';

/**
 * The pictures and videos a registration item carries.
 *
 * The API returns them as one list with a `type` on each row, so the split into
 * two tabs happens here. Order matters — the first picture is the one a store
 * shows — so the list is reorderable, and the new order is written when it is
 * committed rather than on every nudge: one request instead of a dozen.
 *
 * A video needs a thumbnail as well as a URL; the API rejects one without.
 */

function MediaList({
  itemId,
  media,
  isVideo,
  busy,
}: {
  itemId: string | null;
  media: RegistrationMedia[];
  isVideo: boolean;
  /** The list is on screen and being refreshed. */
  busy?: boolean;
}) {
  const { t } = useTranslation();
  const add = useAddMedia(itemId);
  const remove = useRemoveMedia(itemId);
  const reorder = useReorderMedia(itemId);
  const upload = useUploadRegistrationMedia();
  const fileInput = useRef<HTMLInputElement>(null);

  const [order, setOrder] = useState<RegistrationMedia[] | null>(null);
  const [url, setUrl] = useState('');
  const [thumbnail, setThumbnail] = useState('');

  const rows = order ?? media;
  const failed = () => toast.error(t('itemRegistration.actionFailed'));
  const canAdd = !!url.trim() && (!isVideo || !!thumbnail.trim());

  function move(index: number, by: number) {
    const next = [...rows];
    const target = index + by;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setOrder(next);
  }

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <Label className="text-xs font-semibold">
          {isVideo
            ? t('itemRegistration.addVideo')
            : t('itemRegistration.addPicture')}
        </Label>

        {!isVideo && (
          <>
            <input
              ref={fileInput}
              type="file"
              accept="image/*"
              hidden
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (!file) return;
                upload.mutate(file, {
                  onSuccess: (uploaded) =>
                    add.mutate(
                      { url: uploaded, type: MEDIA_TYPE.picture },
                      { onError: failed }
                    ),
                  onError: () => toast.error(t('itemRegistration.uploadFailed')),
                });
                event.target.value = '';
              }}
            />
            <Button
              variant="outline"
              className="w-full border-dashed"
              disabled={upload.isPending || add.isPending}
              onClick={() => fileInput.current?.click()}
            >
              {upload.isPending ? (
                <Loader2 className="animate-spin" />
              ) : (
                <ImagePlus />
              )}
              {t('itemRegistration.uploadImage')}
            </Button>
          </>
        )}

        <form
          className="space-y-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (!canAdd) return;
            add.mutate(
              {
                url: url.trim(),
                thumbnailUrl: thumbnail.trim(),
                type: isVideo ? MEDIA_TYPE.video : MEDIA_TYPE.picture,
              },
              {
                onSuccess: () => {
                  setUrl('');
                  setThumbnail('');
                },
                onError: failed,
              }
            );
          }}
        >
          <div className="flex gap-2">
            <Input
              value={url}
              onChange={(event) => setUrl(event.target.value)}
              placeholder={
                isVideo
                  ? t('itemRegistration.videoUrl')
                  : t('itemRegistration.imageUrl')
              }
              aria-label={t('itemRegistration.addByUrl')}
            />
            {!isVideo && (
              <Button type="submit" disabled={!canAdd || add.isPending}>
                <Plus />
              </Button>
            )}
          </div>

          {/* The API rejects a video with no thumbnail, so it is asked for
              here rather than reported as a failure afterwards. */}
          {isVideo && (
            <div className="flex gap-2">
              <Input
                value={thumbnail}
                onChange={(event) => setThumbnail(event.target.value)}
                placeholder={t('itemRegistration.thumbnailUrl')}
                aria-label={t('itemRegistration.thumbnailUrl')}
              />
              <Button type="submit" disabled={!canAdd || add.isPending}>
                <Plus />
              </Button>
            </div>
          )}
        </form>
      </div>

      {rows.length === 0 ? (
        <p className="text-muted-foreground rounded-xl border border-dashed py-10 text-center text-sm">
          {isVideo
            ? t('itemRegistration.noVideos')
            : t('itemRegistration.noPictures')}
        </p>
      ) : (
        <div className="relative rounded-xl">
          <BusyOverlay show={busy} />
          <div
            className={cn(
              'divide-y rounded-xl border',
              busy && 'opacity-60 transition-opacity'
            )}
          >
            {rows.map((entry, index) => (
              <div key={entry.id} className="flex items-center gap-2 p-2.5">
                <div className="flex shrink-0 flex-col">
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className="size-5"
                    aria-label={t('itemRegistration.moveUp')}
                    disabled={index === 0}
                    onClick={() => move(index, -1)}
                  >
                    <ChevronLeft className="size-3.5 rotate-90" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className="size-5"
                    aria-label={t('itemRegistration.moveDown')}
                    disabled={index === rows.length - 1}
                    onClick={() => move(index, 1)}
                  >
                    <ChevronRight className="size-3.5 rotate-90" />
                  </Button>
                </div>

                {/* A video has a thumbnail of its own; show it rather than a
                    generic glyph when there is one. */}
                {isVideo && !entry.thumbnailUrl ? (
                  <span className="bg-muted flex size-12 shrink-0 items-center justify-center rounded">
                    <Video className="text-muted-foreground size-5" />
                  </span>
                ) : (
                  <img
                    src={(isVideo ? entry.thumbnailUrl : entry.url) ?? ''}
                    alt=""
                    className="bg-muted size-12 shrink-0 rounded object-contain"
                  />
                )}

                <p className="text-muted-foreground min-w-0 flex-1 truncate text-xs">
                  {entry.url}
                </p>

                {/* The first picture is the one a store shows. */}
                {!isVideo && index === 0 && (
                  <span className="text-muted-foreground shrink-0 text-xs">
                    {t('itemRegistration.mainImage')}
                  </span>
                )}

                <Button
                  variant="ghost"
                  size="icon-sm"
                  className="text-destructive"
                  aria-label={t('itemRegistration.remove')}
                  disabled={remove.isPending}
                  onClick={() =>
                    remove.mutate(entry.id, {
                      onSuccess: () => setOrder(null),
                      onError: failed,
                    })
                  }
                >
                  <Trash2 className="size-3.5" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Only offered once the order actually differs. */}
      {order && (
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setOrder(null)}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={reorder.isPending}
            onClick={() =>
              reorder.mutate(order, {
                onSuccess: () => {
                  toast.success(t('itemRegistration.orderSaved'));
                  setOrder(null);
                },
                onError: failed,
              })
            }
          >
            {reorder.isPending && <Loader2 className="animate-spin" />}
            {t('itemRegistration.saveOrder')}
          </Button>
        </div>
      )}
    </div>
  );
}

export function MediaSheet({
  itemId,
  title,
  open,
  onOpenChange,
}: {
  itemId: string | null;
  title: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const media = useRegistrationMedia(open ? itemId : null);

  // One list from the API, split by `type` here.
  const all = media.data ?? [];
  const pictures = all.filter((entry) => entry.type !== MEDIA_TYPE.video);
  const videos = all.filter((entry) => entry.type === MEDIA_TYPE.video);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full gap-0 overflow-y-auto p-0 sm:max-w-lg"
      >
        <SheetHeader className="border-b pr-12">
          <SheetTitle>{title}</SheetTitle>
        </SheetHeader>

        <div className="p-4">
          {media.isPending ? (
            <div className="space-y-3">
              <Skeleton className="h-9 w-48" />
              <div className="divide-y rounded-xl border">
                {Array.from({ length: 3 }).map((_, index) => (
                  <div key={index} className="flex items-center gap-2 p-2.5">
                    <Skeleton className="size-12 shrink-0 rounded" />
                    <Skeleton className="h-3 flex-1" />
                  </div>
                ))}
              </div>
            </div>
          ) : media.isError ? (
            <p className="text-muted-foreground py-16 text-center text-sm">
              {t('itemRegistration.mediaFailed')}
            </p>
          ) : (
            <Tabs defaultValue="pictures">
              <TabsList>
                <TabsTrigger value="pictures">
                  {t('itemRegistration.pictures')} ({pictures.length})
                </TabsTrigger>
                <TabsTrigger value="videos">
                  {t('itemRegistration.videos')} ({videos.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="pictures">
                <MediaList
                  itemId={itemId}
                  media={pictures}
                  isVideo={false}
                  busy={media.isFetching}
                />
              </TabsContent>
              <TabsContent value="videos">
                <MediaList
                  itemId={itemId}
                  media={videos}
                  isVideo
                  busy={media.isFetching}
                />
              </TabsContent>
            </Tabs>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
